import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { MagnifyingGlass, CaretLeft, CaretRight } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const SORT_OPTIONS = [
  { value: 'seo_asc', label: '🔴 Lav SEO først (anbefalt)' },
  { value: 'seo_desc', label: '🟢 Høy SEO først' },
  { value: 'modified_desc', label: 'Sist endret' },
  { value: 'name_asc', label: 'A→Å (alfabetisk)' },
];

/**
 * Reusable products list with filters, sort and optimized/unoptimized toggle.
 * Used inside AutoGuard "Produkter" tab.
 *
 * Props:
 *   filters: { issue, status, category, leverandor } — controlled by parent (so AutoGuard scope syncs)
 *   selectable: boolean — when true, show a checkbox column for bulk-select
 *   selectedIds: Set<number> — controlled set of selected product IDs (parent state)
 *   onSelectionChange: (Set<number>, {totalOnPage, ids}) => void — callback when user toggles a checkbox or "select all"
 */
export default function ProductsList({ filters, selectable = false, selectedIds, onSelectionChange }) {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [optimized, setOptimized] = useState('no');   // 'no' | 'yes' | 'all'
  const [sortBy, setSortBy] = useState('seo_asc');
  const [loading, setLoading] = useState(true);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const params = { page, limit: 50, sort_by: sortBy };
      if (search) params.search = search;
      if (filters?.issue) params.issue = filters.issue;
      if (filters?.status) params.status = filters.status;
      if (filters?.category) params.category = filters.category;
      if (filters?.leverandor) params.leverandor = filters.leverandor;
      if (optimized !== 'all') params.optimized = optimized;
      const { data } = await axios.get(`${API}/api/products`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(data.products);
      setTotal(data.total);
      setPages(data.pages);
      // Save the current page's product IDs in sessionStorage so the detail
      // page can implement Forrige/Neste navigation without re-fetching the
      // list. We store the filter+sort fingerprint too so we can warn if the
      // list has changed since last navigation (optional).
      try {
        sessionStorage.setItem(
          'productNav',
          JSON.stringify({
            ids: (data.products || []).map((p) => p.id),
            page,
            pages: data.pages,
            total: data.total,
            sortBy,
            params,
            savedAt: Date.now(),
          })
        );
      } catch {}
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, search, optimized, filters]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => { setPage(1); }, [filters, optimized, sortBy]);

  const seoColor = (s) => (s >= 80 ? 'text-success' : s >= 50 ? 'text-warning' : 'text-destructive');

  return (
    <div className="space-y-4" data-testid="products-list">
      {/* Top toolbar */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3">
        <form
          onSubmit={(e) => { e.preventDefault(); setPage(1); fetchProducts(); }}
          className="flex gap-2 w-full sm:flex-1 sm:min-w-[220px] sm:max-w-md"
        >
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Søk produkter..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="products-list-search"
            />
          </div>
          <Button type="submit" variant="secondary" size="sm">Søk</Button>
        </form>

        <Select value={optimized} onValueChange={setOptimized}>
          <SelectTrigger className="w-full sm:w-[200px]" data-testid="optimized-toggle">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="no">🔴 Trenger forbedring</SelectItem>
            <SelectItem value="yes">🟢 Ferdig forbedret</SelectItem>
            <SelectItem value="all">Alle</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-full sm:w-[220px]" data-testid="sort-by">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <p className="text-xs text-muted-foreground ml-auto">
          {total.toLocaleString('nb-NO')} produkter
        </p>
      </div>

      {/* Table — horizontal scroll on small screens so columns stay readable. */}
      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30">
              {selectable && (
                <TableHead className="w-[40px] text-center">
                  <input
                    type="checkbox"
                    aria-label="Velg alle på siden"
                    data-testid="select-all-checkbox"
                    checked={
                      products.length > 0 &&
                      products.every((p) => selectedIds?.has(p.id))
                    }
                    onChange={(e) => {
                      const next = new Set(selectedIds || []);
                      if (e.target.checked) {
                        products.forEach((p) => next.add(p.id));
                      } else {
                        products.forEach((p) => next.delete(p.id));
                      }
                      onSelectionChange?.(next, { totalOnPage: products.length, ids: products.map((p) => p.id) });
                    }}
                    className="cursor-pointer"
                  />
                </TableHead>
              )}
              <TableHead className="w-[50px]">Bilde</TableHead>
              <TableHead>Produktnavn</TableHead>
              <TableHead className="w-[100px]">SKU</TableHead>
              <TableHead className="w-[80px]">Status</TableHead>
              <TableHead className="w-[60px] text-center">SEO</TableHead>
              <TableHead className="w-[100px]">Problemer</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={selectable ? 7 : 6}><div className="h-8 bg-muted animate-pulse rounded" /></TableCell>
                </TableRow>
              ))
            ) : products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={selectable ? 7 : 6} className="text-center py-12 text-muted-foreground">
                  {total === 0
                    ? optimized === 'yes'
                      ? '🎉 Ingen produkter er ferdig forbedret ennå.'
                      : optimized === 'no'
                        ? '🎉 Alle produkter er ferdig forbedret!'
                        : 'Ingen produkter synkronisert ennå. Trykk "Sync nå" på Dashboard.'
                    : 'Ingen treff i nåværende filter.'}
                </TableCell>
              </TableRow>
            ) : (
              products.map((p) => {
                const issues = [];
                if (!p.description) issues.push('beskrivelse');
                if (!p.short_description) issues.push('kort beskr.');
                if (!p.images || p.images.length === 0) issues.push('bilder');
                if (!p.sku) issues.push('SKU');
                const isSelected = !!selectedIds?.has(p.id);
                return (
                  <TableRow
                    key={p.id}
                    className={`cursor-pointer hover:bg-muted/30 ${isSelected ? 'bg-primary/5' : ''}`}
                    onClick={(e) => {
                      // Clicks on the checkbox cell should NOT navigate.
                      if (e.target.closest('[data-row-checkbox]')) return;
                      navigate(`/products/${p.id}`);
                    }}
                    data-testid={`product-row-${p.id}`}
                  >
                    {selectable && (
                      <TableCell className="py-2 px-3 text-center" data-row-checkbox>
                        <input
                          type="checkbox"
                          aria-label={`Velg ${p.name}`}
                          data-testid={`select-product-${p.id}`}
                          checked={isSelected}
                          onChange={(e) => {
                            const next = new Set(selectedIds || []);
                            if (e.target.checked) next.add(p.id);
                            else next.delete(p.id);
                            onSelectionChange?.(next, { totalOnPage: products.length, ids: products.map((x) => x.id) });
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="cursor-pointer"
                        />
                      </TableCell>
                    )}
                    <TableCell className="py-2 px-3">
                      {p.images?.[0] ? (
                        <img
                          src={`${API}/api/media/proxy?url=${encodeURIComponent(p.images[0].src)}`}
                          alt=""
                          className="w-10 h-10 object-cover rounded border border-border"
                          loading="lazy"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-10 h-10 bg-muted rounded border" />
                      )}
                    </TableCell>
                    <TableCell className="py-2 px-3">
                      <p className="text-sm font-medium truncate max-w-[400px]">{p.name}</p>
                      {p.modified_locally && <Badge variant="outline" className="text-xs mt-1">Endret lokalt</Badge>}
                    </TableCell>
                    <TableCell className="py-2 px-3 font-mono text-xs">{p.sku || '-'}</TableCell>
                    <TableCell className="py-2 px-3 text-xs">{p.status || '-'}</TableCell>
                    <TableCell className="py-2 px-3 text-center">
                      <span className={`font-mono text-sm font-semibold ${seoColor(p.seo_score)}`}>
                        {p.seo_score || 0}
                      </span>
                    </TableCell>
                    <TableCell className="py-2 px-3">
                      {issues.length > 0 ? (
                        <span className="text-xs text-destructive">{issues.length} mangler</span>
                      ) : (
                        <span className="text-xs text-success">OK</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">Side {page} av {pages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <CaretLeft size={14} /> Forrige
            </Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
              Neste <CaretRight size={14} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
